// What the City Overview (the City Hall screen, Ben's Oct 8 concept) reads off one village/get: the queue rows, the farm's
// population split, the troop table, the city's defense with the wall, the resource rows. Pure functions, no React.
import type { Village } from "@/api/types";
import { BUILDING_NAMES, RESOURCE_KEYS, TROOP_KEYS, TROOP_NAMES, TROOP_PLURAL, type BuildingKey, type ResourceKey, type TroopKey } from "./constants";
import { buildingMap, constructionQueue, fullInMs, populationBreakdown, queueProgress, queueSlots, recruitDone, recruitQueue, researchLevel, researchQueue } from "./city";
import { UNITS, WALL_FLAT, WALL_PCT, unitStats } from "./rules";
import { fmtNum } from "./format";

// ------------------------------------------------------------------ queues

export type QueueKind = "build" | "recruit" | "research" | "academy";

export interface OverviewQueue {
  key: "build" | BuildingKey;
  kind: QueueKind;
  /** BUILD · BARRACKS · … */
  label: string;
  /** "5/5" on the build row */
  count?: string;
  line: string;
  /** a second, quieter part of the line ("4 more queued") */
  more?: string;
  busy: boolean;
  endMs?: number;
  progress?: number;
}

/** The Queues card: the build queue, then each recruiting building that is built (Barracks, Stables, Workshop), the Forge
 *  and the Academy. Idle rows say so (the screen offers GO TO). */
export function overviewQueues(v: Village, now: number): OverviewQueue[] {
  const map = buildingMap(v);
  const built = (k: BuildingKey) => (map[k]?.level ?? 0) > 0;
  const rows: OverviewQueue[] = [];

  const q = constructionQueue(v);
  const slots = queueSlots(v);
  const first = q[0];
  rows.push(
    first
      ? {
          key: "build", kind: "build", label: "Build", count: `${q.length}/${slots}`, busy: true,
          line: `${BUILDING_NAMES[first.item.key] ?? first.item.name} ${first.item.is_upgrade ? "to" : "down to"} Lv. ${first.item.level}`,
          more: q.length > 1 ? `${q.length - 1} more queued` : undefined,
          endMs: first.endMs, progress: queueProgress(first, now),
        }
      : { key: "build", kind: "build", label: "Build", count: `0/${slots}`, busy: false, line: "Nothing building — every slot is free" },
  );

  for (const bk of ["b_b", "b_st", "b_wo"] as BuildingKey[]) {
    if (!built(bk)) continue;
    const jobs = recruitQueue(v, bk);
    const j = jobs[0];
    if (j) {
      const done = recruitDone(j, now);
      rows.push({
        key: bk, kind: "recruit", label: BUILDING_NAMES[bk], busy: true,
        line: `${fmtNum(j.item.num_units)} ${j.item.num_units === 1 ? TROOP_NAMES[j.troop] : TROOP_PLURAL[j.troop]} · ${fmtNum(done)} trained`,
        more: jobs.length > 1 ? `+${jobs.length - 1} batch${jobs.length > 2 ? "es" : ""}` : undefined,
        endMs: j.endMs, progress: j.endMs > j.startMs ? Math.min(1, Math.max(0, (now - j.startMs) / (j.endMs - j.startMs))) : 1,
      });
    } else rows.push({ key: bk, kind: "recruit", label: BUILDING_NAMES[bk], busy: false, line: "Idle — nothing recruiting" });
  }

  if (built("b_s")) {
    const r = researchQueue(v)[0];
    if (r) {
      const start = r.endMs - r.durationMs;
      rows.push({
        key: "b_s", kind: "research", label: "Forge", busy: true, line: `${r.item.name} research to Lv. ${r.item.level}`,
        endMs: r.endMs, progress: r.durationMs > 0 ? Math.min(1, Math.max(0, (now - start) / r.durationMs)) : 1,
      });
    } else rows.push({ key: "b_s", kind: "research", label: "Forge", busy: false, line: "Idle — nothing researching" });
  }

  if (built("b_a")) {
    const a = recruitQueue(v, "b_a")[0];
    if (a) {
      rows.push({
        key: "b_a", kind: "academy", label: "Academy", busy: true, line: `Educating ${fmtNum(a.item.num_units)} Scholar${a.item.num_units === 1 ? "" : "s"}`,
        endMs: a.endMs, progress: a.endMs > a.startMs ? Math.min(1, Math.max(0, (now - a.startMs) / (a.endMs - a.startMs))) : 1,
      });
    } else rows.push({ key: "b_a", kind: "academy", label: "Academy", busy: false, line: "Idle — no scholar in education" });
  }
  return rows;
}

// ------------------------------------------------------------------ resources

export interface ResourceRow {
  r: ResourceKey;
  amount: number;
  cap: number;
  rate: number;
  building: BuildingKey;
  level: number;
  fullInMs: number;
}

const PRODUCER: Record<ResourceKey, BuildingKey> = { r_w: "b_w", r_c: "b_c", r_i: "b_i" };

export function resourceRows(v: Village): ResourceRow[] {
  const map = buildingMap(v);
  return RESOURCE_KEYS.map((r) => ({
    r,
    amount: Math.floor(v.resources?.[r] ?? 0),
    cap: v.resources_capacity ?? 0,
    rate: v.resources_production?.[r] ?? 0,
    building: PRODUCER[r],
    level: map[PRODUCER[r]]?.level ?? 0,
    fullInMs: fullInMs(v, r),
  }));
}

// ------------------------------------------------------------------ farm space

export interface FarmSplit {
  used: number;
  max: number;
  home: number;
  away: number;
  training: number;
  buildings: number;
  free: number;
}

const popOf = (c: Partial<Record<TroopKey, number>> | undefined) => TROOP_KEYS.reduce((a, k) => a + (c?.[k] ?? 0) * (UNITS[k]?.pop ?? 0), 0);

/** Where the farm's population goes. Buildings and training come from the Farm's own table when the game sends it;
 *  home and away from the troop counts × each unit's population. */
export function farmSplit(v: Village): FarmSplit {
  const max = v.max_population ?? 0;
  const used = v.current_population ?? 0;
  const home = popOf(v.troops_in_village);
  const away = popOf(v.total_troops_sent);
  const pb = populationBreakdown(v);
  const training = pb ? pb.troopsQueued : recruitQueue(v).reduce((a, e) => a + Math.max(0, e.item.num_units - (e.item.completed_units ?? 0)) * (UNITS[e.troop]?.pop ?? 0), 0);
  const buildings = pb ? pb.buildings + pb.buildingsQueued : Math.max(0, used - home - away - training);
  return { used, max, home, away, training, buildings, free: Math.max(0, max - used) };
}

// ------------------------------------------------------------------ troops

export interface TroopRow {
  k: TroopKey;
  level: number;
  /** research queued in the Forge for this unit */
  researching: boolean;
  home: number;
  away: number;
  training: number;
  total: number;
}

/** The troop table: every unit (not scholars) the city has, is training, or has researched. */
export function troopRows(v: Village): TroopRow[] {
  const inForge = new Set(researchQueue(v).map((r) => r.troop).filter(Boolean));
  const training: Partial<Record<TroopKey, number>> = {};
  for (const e of recruitQueue(v)) training[e.troop] = (training[e.troop] ?? 0) + Math.max(0, e.item.num_units - (e.item.completed_units ?? 0));
  return TROOP_KEYS.filter((k) => k !== "t_n")
    .map((k) => {
      const home = v.troops_in_village?.[k] ?? 0;
      const away = v.total_troops_sent?.[k] ?? 0;
      const tr = training[k] ?? 0;
      return { k, level: researchLevel(v, k), researching: inForge.has(k), home, away, training: tr, total: home + away + tr };
    })
    .filter((r) => r.total > 0 || r.level > 0);
}

/** Attack strength of the troops at home, at their research levels. */
export const attackAtHome = (v: Village): number =>
  TROOP_KEYS.reduce((a, k) => a + (v.troops_in_village?.[k] ?? 0) * unitStats(k, Math.max(1, researchLevel(v, k))).att, 0);

// ------------------------------------------------------------------ defense

export interface CityDefense {
  wall: number;
  flat: number;
  /** 0.89 = +89% */
  pct: number;
  /** (Σ home troops' defense + the wall's flat bonus) × (1 + the wall's percentage) — the combat formula's defender side */
  infantry: number;
  mounted: number;
  supportUnits: number;
  /** "400 Sentries" when one kind of unit is stationed here, else "N units" */
  supportLabel: string;
}

export function cityDefense(v: Village): CityDefense {
  const wall = Math.min(25, buildingMap(v).b_wa?.level ?? 0);
  const flat = WALL_FLAT[wall] ?? 20;
  const pct = WALL_PCT[wall] ?? 0;
  let inf = 0;
  let cav = 0;
  for (const k of TROOP_KEYS) {
    const n = v.troops_in_village?.[k] ?? 0;
    if (!n) continue;
    const s = unitStats(k, Math.max(1, researchLevel(v, k)));
    inf += n * s.infdef;
    cav += n * s.cavdef;
  }
  const support: Partial<Record<TroopKey, number>> = {};
  for (const s of v.support?.received ?? []) for (const k of TROOP_KEYS) support[k] = (support[k] ?? 0) + (s.units?.[k] ?? 0);
  const kinds = TROOP_KEYS.filter((k) => (support[k] ?? 0) > 0);
  const supportUnits = kinds.reduce((a, k) => a + (support[k] ?? 0), 0);
  const supportLabel = !supportUnits ? "None" : kinds.length === 1 ? `${fmtNum(supportUnits)} ${supportUnits === 1 ? TROOP_NAMES[kinds[0]] : TROOP_PLURAL[kinds[0]]}` : `${fmtNum(supportUnits)} units`;
  return { wall, flat, pct, infantry: Math.round((inf + flat) * (1 + pct)), mounted: Math.round((cav + flat) * (1 + pct)), supportUnits, supportLabel };
}

/** "4 Sentries" / "1,200 Berserkers" / "1,250 units" for a set of troops. */
export function troopWords(c: Partial<Record<TroopKey, number>> | undefined): string {
  const kinds = TROOP_KEYS.filter((k) => (c?.[k] ?? 0) > 0);
  const n = kinds.reduce((a, k) => a + (c?.[k] ?? 0), 0);
  if (!n) return "No troops";
  if (kinds.length === 1) return `${fmtNum(n)} ${n === 1 ? TROOP_NAMES[kinds[0]] : TROOP_PLURAL[kinds[0]]}`;
  const top = [...kinds].sort((a, b) => (c![b] ?? 0) - (c![a] ?? 0))[0];
  return `${fmtNum(n)} troops, mostly ${TROOP_PLURAL[top]}`;
}
